import { withRequestDedupe } from '../requestDedupe';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../firebase';
import axios from '../../utils/axios';
import { showAlert } from './alertSlice';

const fetchListInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
  lastSeen: null,
  emptyList: null,
};

const fetchSingleInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
};

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
};

const initialState = {
  allArtifacts: { ...fetchListInitialState },
  artifactById: { ...fetchSingleInitialState },
  artifactRisks: { ...fetchSingleInitialState },
  artifactStatus: { ...fetchSingleInitialState },
  transcriptStatus: { ...fetchSingleInitialState },
  updateArtifact: { ...mutationInitialState },
  processArtifact: { ...mutationInitialState },
  updateArtifactDealAssociation: { ...mutationInitialState },
  reAnalyzeCall: { ...mutationInitialState },
  reAnalyzeCallV2: { ...mutationInitialState },
  bots: {
    fetching: null,
    fetchingSuccessful: null,
    fetchingFailed: null,
    error: null,
    errorText: null,
    data: [],
  },
  uploadAudio: { ...mutationInitialState },
};

// --- Async Thunks ---

export const fetchAllArtifacts = createAsyncThunk(
  'artifacts/fetchAllArtifacts',
  async ({ limit, lastSeen, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(
              `/us/qai/deals/interactions/v3`,
              payload,
              {
                params: {
                  requestId: uuidv4(),
                  limit,
                  lastSeen,
                  // interactionSource: 'meeting_recorder',
                },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) {
              resolve({
                data: response.data,
                lastSeenArg: lastSeen,
                lastSeenResult: response.data?.[response.data?.length - 1]?.id,
              });
            } else {
              reject(rejectWithValue('Failed to fetch artifacts'));
            }
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchArtifactById = createAsyncThunk(
  'artifacts/fetchArtifactById',
  async ({ interactionId, skipStateUpdate }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(
              `/us/qai/deals/interactions/${interactionId}`,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200)
              resolve({ data: response.data, skipStateUpdate });
            else reject(rejectWithValue('Failed to get artifact by id'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const updateArtifact = createAsyncThunk(
  'artifacts/updateArtifact',
  async ({ dealId, interactionId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/qai/deals/interactions/${interactionId}/v2`,
              payload,
              {
                params: { 'request-id': uuidv4(), dealId },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to update artifact'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const processArtifact = createAsyncThunk(
  'artifacts/processArtifact',
  async ({ dealId, interactionId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/qai/deals/${dealId}/interactions/${interactionId}/post-process`,
              payload,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to process artifact'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const updateArtifactDealAssociation = createAsyncThunk(
  'artifacts/updateArtifactDealAssociation',
  async ({ fromDealId, toDealId, interactionId }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.put(
              `/us/qai/deals/interactions/${interactionId}/destination/${toDealId}/v2`,
              null,
              {
                params: { requestId: uuidv4(), fromDealId },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else
              reject(
                rejectWithValue('Failed to update artifact deal association'),
              );
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const fetchArtifactRisks = createAsyncThunk(
  'artifacts/fetchArtifactRisks',
  async (interactionId, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(
              `/us/qai/deals/interactions/${interactionId}/risks`,
              {
                params: { requestId: uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to fetch artifact risks'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const checkArtifactStatus = createAsyncThunk(
  'artifacts/checkArtifactStatus',
  async (interactionId, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(
              `/us/qai/deals/interactions/status`,
              [interactionId],
              {
                params: { 'request-id': uuidv4() },
                headers: {
                  Authorization: `Bearer ${token}`,
                  'x-api-version': 'v2',
                },
              },
            );
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message: 'Something went wrong. Please try again',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to check artifact status'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Something went wrong. Please try again',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const checkTranscriptStatus = createAsyncThunk(
  'artifacts/checkTranscriptStatus',
  async (saleReviewId, { dispatch, rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(
              `/cs/sale-reviews/${saleReviewId}/status`,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else {
              dispatch(
                showAlert({
                  message: 'Something went wrong. Please try again',
                  showCross: true,
                  type: 'error',
                  autoHideDuration: 2000,
                }),
              );
              reject(rejectWithValue('Failed to check transcript status'));
            }
          } catch (e) {
            dispatch(
              showAlert({
                message: 'Something went wrong. Please try again',
                showCross: true,
                type: 'error',
                autoHideDuration: 2000,
              }),
            );
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const reAnalyzeCall = createAsyncThunk(
  'artifacts/reAnalyzeCall',
  async (mediaId, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.get(
              `/cs/sale-review/${mediaId}/re-trigger`,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to re-analyze call'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const reAnalyzeCallV2 = createAsyncThunk(
  'artifacts/reAnalyzeCallV2',
  async ({ mediaId, payload }, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (user) => {
          unsubscribe();
          if (!user) return reject(rejectWithValue('User not authenticated'));
          try {
            const token = await user.getIdToken();
            const response = await axios.post(
              `/cs/sale-review/${mediaId}/re-trigger/v2`,
              payload,
              {
                params: { 'request-id': uuidv4() },
                headers: { Authorization: `Bearer ${token}` },
              },
            );
            if (response.status === 200) resolve(response.data);
            else reject(rejectWithValue('Failed to re-analyze call'));
          } catch (e) {
            reject(rejectWithValue(e.message));
          }
        },
        (error) => reject(rejectWithValue(error.message)),
      );
    });
  },
);

export const uploadAudio = createAsyncThunk(
  'artifacts/uploadAudio',
  async ({ signedURL, audioData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(signedURL, audioData, {
        headers: { 'Content-Type': audioData?.type?.split(';')?.[0] },
      });
      if (response.status === 200) return response.data;
      return rejectWithValue('Failed to upload audio');
    } catch (e) {
      return rejectWithValue(e?.response?.data || e.message);
    }
  },
);

export const fetchBots = createAsyncThunk(
  'artifacts/fetchBots',
  // Two components request the bot list in the same tick on /ask-ai.
  withRequestDedupe(
    (args) =>
      `bots:${args?.type ?? 'any'}:${args?.limit ?? 10}:${args?.lastSeen ??
        'first'}`,
    async ({ type, limit = 10, lastSeen = null }, { rejectWithValue }) => {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(
          auth,
          async (user) => {
            unsubscribe();
            if (!user) return reject(rejectWithValue('User not authenticated'));
            try {
              const token = await user.getIdToken();
              const response = await axios.get(`/cs/v2/bots`, {
                params: { 'request-id': uuidv4(), type, limit, lastSeen },
                headers: { Authorization: `Bearer ${token}` },
              });
              if (response.status === 200) resolve(response.data || []);
              else reject(rejectWithValue('Failed to fetch bots'));
            } catch (e) {
              reject(rejectWithValue(e.message));
            }
          },
          (error) => reject(rejectWithValue(error.message)),
        );
      });
    },
  ),
);

const artifactsSlice = createSlice({
  name: 'artifacts',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // --- All Artifacts ---
    builder
      .addCase(fetchAllArtifacts.pending, (state, action) => {
        const { lastSeen } = action.meta.arg;
        state.allArtifacts.fetching = !lastSeen;
        state.allArtifacts.fetchingSuccessful = false;
        state.allArtifacts.fetchingFailed = false;
        state.allArtifacts.error = false;
        state.allArtifacts.errorText = null;
      })
      .addCase(fetchAllArtifacts.fulfilled, (state, action) => {
        const { data, lastSeenArg, lastSeenResult } = action.payload;
        state.allArtifacts.fetching = false;
        state.allArtifacts.fetchingSuccessful = true;
        state.allArtifacts.fetchingFailed = false;
        state.allArtifacts.error = false;
        state.allArtifacts.errorText = null;
        if (lastSeenArg) {
          state.allArtifacts.data = state.allArtifacts.data
            ? state.allArtifacts.data.concat(data)
            : data;
          state.allArtifacts.emptyList =
            Array.isArray(data) && (data.length === 0 || data.length % 10 > 0);
        } else {
          state.allArtifacts.data = data;
          state.allArtifacts.emptyList = false;
        }
        state.allArtifacts.lastSeen =
          lastSeenResult || state.allArtifacts.lastSeen;
      })
      .addCase(fetchAllArtifacts.rejected, (state, action) => {
        state.allArtifacts.fetching = false;
        state.allArtifacts.fetchingSuccessful = false;
        state.allArtifacts.fetchingFailed = true;
        state.allArtifacts.error = true;
        state.allArtifacts.errorText = action.payload;
      });

    // --- Artifact By Id ---
    builder
      .addCase(fetchArtifactById.pending, (state) => {
        state.artifactById.fetching = true;
        state.artifactById.fetchingSuccessful = false;
        state.artifactById.fetchingFailed = false;
        state.artifactById.error = false;
        state.artifactById.errorText = null;
      })
      .addCase(fetchArtifactById.fulfilled, (state, action) => {
        const { data, skipStateUpdate } = action.payload;
        state.artifactById.fetching = false;
        state.artifactById.fetchingSuccessful = true;
        state.artifactById.fetchingFailed = false;
        state.artifactById.error = false;
        state.artifactById.errorText = null;
        if (!skipStateUpdate) state.artifactById.data = data;
      })
      .addCase(fetchArtifactById.rejected, (state, action) => {
        state.artifactById.fetching = false;
        state.artifactById.fetchingSuccessful = false;
        state.artifactById.fetchingFailed = true;
        state.artifactById.error = true;
        state.artifactById.errorText = action.payload;
      });

    // --- Update Artifact ---
    builder
      .addCase(updateArtifact.pending, (state) => {
        state.updateArtifact.processing = true;
        state.updateArtifact.done = false;
        state.updateArtifact.failed = false;
        state.updateArtifact.error = null;
        state.updateArtifact.errorText = null;
      })
      .addCase(updateArtifact.fulfilled, (state) => {
        state.updateArtifact.processing = false;
        state.updateArtifact.done = true;
        state.updateArtifact.failed = false;
        state.updateArtifact.error = null;
        state.updateArtifact.errorText = null;
      })
      .addCase(updateArtifact.rejected, (state, action) => {
        state.updateArtifact.processing = false;
        state.updateArtifact.done = false;
        state.updateArtifact.failed = true;
        state.updateArtifact.error = true;
        state.updateArtifact.errorText = action.payload;
      });

    // --- Process Artifact ---
    builder
      .addCase(processArtifact.pending, (state) => {
        state.processArtifact.processing = true;
        state.processArtifact.done = false;
        state.processArtifact.failed = false;
        state.processArtifact.error = null;
        state.processArtifact.errorText = null;
      })
      .addCase(processArtifact.fulfilled, (state) => {
        state.processArtifact.processing = false;
        state.processArtifact.done = true;
        state.processArtifact.failed = false;
        state.processArtifact.error = null;
        state.processArtifact.errorText = null;
      })
      .addCase(processArtifact.rejected, (state, action) => {
        state.processArtifact.processing = false;
        state.processArtifact.done = false;
        state.processArtifact.failed = true;
        state.processArtifact.error = true;
        state.processArtifact.errorText = action.payload;
      });

    // --- Update Artifact Deal Association ---
    builder
      .addCase(updateArtifactDealAssociation.pending, (state) => {
        state.updateArtifactDealAssociation.processing = true;
        state.updateArtifactDealAssociation.done = false;
        state.updateArtifactDealAssociation.failed = false;
        state.updateArtifactDealAssociation.error = null;
        state.updateArtifactDealAssociation.errorText = null;
      })
      .addCase(updateArtifactDealAssociation.fulfilled, (state) => {
        state.updateArtifactDealAssociation.processing = false;
        state.updateArtifactDealAssociation.done = true;
        state.updateArtifactDealAssociation.failed = false;
        state.updateArtifactDealAssociation.error = null;
        state.updateArtifactDealAssociation.errorText = null;
      })
      .addCase(updateArtifactDealAssociation.rejected, (state, action) => {
        state.updateArtifactDealAssociation.processing = false;
        state.updateArtifactDealAssociation.done = false;
        state.updateArtifactDealAssociation.failed = true;
        state.updateArtifactDealAssociation.error = true;
        state.updateArtifactDealAssociation.errorText = action.payload;
      });

    // --- Artifact Risks ---
    builder
      .addCase(fetchArtifactRisks.pending, (state) => {
        state.artifactRisks.fetching = true;
        state.artifactRisks.fetchingSuccessful = false;
        state.artifactRisks.fetchingFailed = false;
        state.artifactRisks.error = false;
        state.artifactRisks.errorText = null;
      })
      .addCase(fetchArtifactRisks.fulfilled, (state, action) => {
        state.artifactRisks.fetching = false;
        state.artifactRisks.fetchingSuccessful = true;
        state.artifactRisks.fetchingFailed = false;
        state.artifactRisks.error = false;
        state.artifactRisks.errorText = null;
        state.artifactRisks.data = action.payload;
      })
      .addCase(fetchArtifactRisks.rejected, (state, action) => {
        state.artifactRisks.fetching = false;
        state.artifactRisks.fetchingSuccessful = false;
        state.artifactRisks.fetchingFailed = true;
        state.artifactRisks.error = true;
        state.artifactRisks.errorText = action.payload;
      });

    // --- Re-Analyze Call ---
    builder
      .addCase(reAnalyzeCall.pending, (state) => {
        state.reAnalyzeCall.processing = true;
        state.reAnalyzeCall.done = false;
        state.reAnalyzeCall.failed = false;
        state.reAnalyzeCall.error = null;
        state.reAnalyzeCall.errorText = null;
      })
      .addCase(reAnalyzeCall.fulfilled, (state) => {
        state.reAnalyzeCall.processing = false;
        state.reAnalyzeCall.done = true;
        state.reAnalyzeCall.failed = false;
        state.reAnalyzeCall.error = null;
        state.reAnalyzeCall.errorText = null;
      })
      .addCase(reAnalyzeCall.rejected, (state, action) => {
        state.reAnalyzeCall.processing = false;
        state.reAnalyzeCall.done = false;
        state.reAnalyzeCall.failed = true;
        state.reAnalyzeCall.error = true;
        state.reAnalyzeCall.errorText = action.payload;
      });

    // --- Re-Analyze Call V2 ---
    builder
      .addCase(reAnalyzeCallV2.pending, (state) => {
        state.reAnalyzeCallV2.processing = true;
        state.reAnalyzeCallV2.done = false;
        state.reAnalyzeCallV2.failed = false;
        state.reAnalyzeCallV2.error = null;
        state.reAnalyzeCallV2.errorText = null;
      })
      .addCase(reAnalyzeCallV2.fulfilled, (state) => {
        state.reAnalyzeCallV2.processing = false;
        state.reAnalyzeCallV2.done = true;
        state.reAnalyzeCallV2.failed = false;
        state.reAnalyzeCallV2.error = null;
        state.reAnalyzeCallV2.errorText = null;
      })
      .addCase(reAnalyzeCallV2.rejected, (state, action) => {
        state.reAnalyzeCallV2.processing = false;
        state.reAnalyzeCallV2.done = false;
        state.reAnalyzeCallV2.failed = true;
        state.reAnalyzeCallV2.error = true;
        state.reAnalyzeCallV2.errorText = action.payload;
      });

    // --- Upload Audio ---
    builder
      .addCase(uploadAudio.pending, (state) => {
        state.uploadAudio.processing = true;
        state.uploadAudio.done = false;
        state.uploadAudio.failed = false;
      })
      .addCase(uploadAudio.fulfilled, (state) => {
        state.uploadAudio.processing = false;
        state.uploadAudio.done = true;
        state.uploadAudio.failed = false;
      })
      .addCase(uploadAudio.rejected, (state, action) => {
        state.uploadAudio.processing = false;
        state.uploadAudio.done = false;
        state.uploadAudio.failed = true;
        state.uploadAudio.error = action.payload;
      });

    // --- Bots ---
    builder
      .addCase(fetchBots.pending, (state) => {
        state.bots.fetching = true;
        state.bots.fetchingSuccessful = false;
        state.bots.fetchingFailed = false;
        state.bots.error = false;
        state.bots.errorText = null;
      })
      .addCase(fetchBots.fulfilled, (state, action) => {
        state.bots.fetching = false;
        state.bots.fetchingSuccessful = true;
        state.bots.fetchingFailed = false;
        state.bots.error = false;
        state.bots.errorText = null;
        state.bots.data = action.payload;
      })
      .addCase(fetchBots.rejected, (state, action) => {
        state.bots.fetching = false;
        state.bots.fetchingSuccessful = false;
        state.bots.fetchingFailed = true;
        state.bots.error = true;
        state.bots.errorText = action.payload;
      });
  },
});

export default artifactsSlice.reducer;
